import moment from "moment";

import { AutogenerateReportStrings, ReportStrings,Weekdays } from "constants/constantStrings";
import { LOCAL_TIME_ZONE } from "constants/globalConstants";

import { getItemSession } from "./session";
import { capitalizeFirstLetter, formatDate } from "./utils";

export const isAnywidgetEnable = reports => reports.some(report => report.Enabled);
export const isAllwidgetEnable = reports => {
  const allWidgets = reports.filter(report => report.Enabled);
  return allWidgets.length === reports.length;
};

export const populateXYaxis = (timeData, tag, type) => {
  const trace = {};
  trace["type"] = type;
  trace["x"] = timeData || "";
  trace["y"] = tag.Values || "";
  trace["name"] = tag.TagName || "";
  return trace;
};

export const populateXYNormalize = (timeData, tag, type, index) => {
  const trace = populateXYaxis(timeData, tag, type);
  trace["mode"] = "markers";
  trace["yaxis"] = "y" + index;
  const marker = {};
  marker["size"] = 2;
  trace["marker"] = marker;
  trace.name = `${tag.TagName} (${tag.Unit})`;
  return trace;
};

export const populateXYaxisTime = (timeData, tag, type, index) => {
  const trace = populateXYaxis(timeData, tag, type);
  trace["yaxis"] = "y" + index;
  const marker = {};
  marker["mode"] = "markers";
  trace["marker"] = marker;
  trace.name = `${tag.TagName} (${tag.Unit})`;
  return trace;
};

export const populateXYaxisBar = (timeData, tag, type, index, color) => {
  const trace = populateXYaxis(timeData, tag, type);
  trace["offsetgroup"] = index;
  trace["yaxis"] = "y" + index;
  const marker = {};
  marker["mode"] = "markers";
  marker["color"] = color;
  trace["marker"] = marker;
  trace.name = `${tag.TagName} (${tag.Unit})`;
  return trace;
};

export const populateXYaxisPrediction = (timeData, isPastData, y, dash, name) => {
  const trace = {};
  if (timeData && y && dash && name) {
    trace["x"] = timeData || "";
    trace["y"] = isPastData ? y : new Array(timeData.length - y.length).fill(null).concat(y);
    trace["name"] = name;
    trace["mode"] = "lines";
    const line = {};
    line["dash"] = dash;
    trace["line"] = line;
  }
  return trace;
};


export const getReportPreviewStart = () => {
  let startDate = getItemSession("startDateDisplay");
  if (!startDate) {
    const pastDate = new Date();
    pastDate.setDate(pastDate.getDate() - 7);
    startDate = pastDate;
  }
  return formatDate(startDate);
};

export const getReportPreviewEnd = () => {
  const endDate = getItemSession("endDateDisplay") || new Date();
  return formatDate(endDate);
};
export const getReportDurationRangesUnix = () => {
  let startDate = getItemSession("startDateDisplay");
  if (!startDate) {
    const pastDate = new Date();
    pastDate.setDate(pastDate.getDate() - 7);
    startDate = pastDate;
  }
  const endDate = getItemSession("endDateDisplay") || new Date();
  return { startTime: moment(startDate).unix(), endTime: moment(endDate).unix() };
};

export const isValidResponse = response => response && response.status >= 200 && response.status < 300;

export const isReportPageRendering = page => page === ReportStrings.report;

export const getSchedulingdata = autogenerate => {
  if (
    (autogenerate.interval === AutogenerateReportStrings.daily && autogenerate.hour !== "") ||
    ((autogenerate.interval === AutogenerateReportStrings.weekly || autogenerate.interval === AutogenerateReportStrings.monthly) &&
      autogenerate.day !== "" &&
      autogenerate.hour !== "")
  ) {
    const hourFormatted = autogenerate.hour[0] === "0" ? autogenerate.hour.substring(1, 2) : autogenerate.hour.substring(0, 2);
    let dayFormatted = "";
    if (autogenerate.interval === AutogenerateReportStrings.weekly) {
      dayFormatted = Weekdays.findIndex(day => day === autogenerate.day);
    }
    if (autogenerate.interval === AutogenerateReportStrings.monthly) {
      if (isNaN(autogenerate.day.charAt(0)) === false) {
        dayFormatted = autogenerate.day.slice(0, -2);
      } else {
        dayFormatted = autogenerate.day;
      }
    }

    const autogenerateObject = {
      Enabled: autogenerate.enabled,
      Type: autogenerate.interval?.toLowerCase(),
      Day: parseInt(dayFormatted),
      Hour: parseInt(hourFormatted),
      Timezone: LOCAL_TIME_ZONE
    };
    return autogenerateObject;
  }
};

export const getAutogenerateData = response => {
  if (response.Type) {
    let autogenerate = {
      interval: capitalizeFirstLetter(response.Type),
      savedInterval: "",
      dayInputValue: "",
      day: "",
      savedDay: "",
      hourInputValue: "",
      hour: "",
      savedHour: "",
      enabled: response.Enabled,
      timeZone: LOCAL_TIME_ZONE
    };
    const autogenerateHour = response.Hour < 1 ? "0" + response.Hour : response.Hour;

    autogenerate = { ...autogenerate, hour: autogenerateHour + ":00", hourInputValue: autogenerateHour + ":00" };
    let autogenerateDay = "";
    if (response.Type === AutogenerateReportStrings.monthly.toLocaleLowerCase()) {
      autogenerateDay =
        response.Day === "1" || response.Day === "21"
          ? response.Day + "st"
          : response.Day === "2" || response.Day === "22"
          ? response.Day + "nd"
          : response.Day === "3" || response.Day === "23"
          ? response.Day + "rd"
          : response.Day !== "1" &&
            response.Day !== "21" &&
            response.Day !== "2" &&
            response.Day !== "22" &&
            response.Day !== "3" &&
            response.Day !== "23" &&
            response.Day + "th";
    } else if (response.Type === AutogenerateReportStrings.weekly.toLowerCase()) {
      autogenerateDay = Weekdays[parseInt(response.Day)];
    }
    autogenerate = { ...autogenerate, day: autogenerateDay, dayInputValue: autogenerateDay };
    return autogenerate;
  }
};

export const formatTimeSeriesData = data => {
  const chartData = { ...data.Data };
  const timeSeriesData = [];
  const Yaxis = (timeSeriesData.Yaxis = []);
  timeSeriesData.Time = chartData.Time;
  Object.entries(data.Data).forEach(tags => {
    const tag = data.Tags.filter(x => x.TagId === tags[0]);
    if (tag[0]) {
      const yAxisObj = {
        TagName: tag[0].TagName,
        Unit: tag[0].Units,
        Values: tags[1]
      };
      Yaxis.push(yAxisObj);
    }
  });
  return timeSeriesData;
};

export const isCurrentLayoutEmpty = currentLayout => !currentLayout || Object.keys(currentLayout).length === 0;

export const DURATION_RANGE = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
  CUSTOM: "custom"
};
export const DURATION_SESSION_KEY = {
  DATE_RANGE: "dateRange",
  START_DATE_DISPLAY: "startDateDisplay",
  END_DATE_DISPLAY: "endDateDisplay",
  START_DATE: "startDate",
  END_DATE: "endDate"
};
